/**
 * Schema Registry - Centralized Table Definitions
 *
 * All IndexedDB tables must be defined here. Bump SCHEMA_VERSION when:
 *
 * - Adding a new table
 * - Changing primary key or indexes
 * - Changing encryption settings
 *
 * IMPORTANT: When changing a table's primary key, IndexedDB cannot perform an in-place upgrade. The IndexedDBStore will
 * automatically detect this and:
 *
 * 1. Clear the entire database (all tables)
 * 2. Recreate it with the new schema
 * 3. Log a warning to the browser console
 *
 * All lookup data will be refetched from API on next access — this is acceptable since lookups are transient cached
 * data (7-30 day expiry). Use this approach only for lookup/reference tables, NOT for persistent PHI data.
 *
 * DO NOT use dynamic table creation (addTable) for production tables. This ensures deterministic versioning across all
 * users.
 *
 * @example
 *   // Adding a new table:
 *   // 1. Add entry to LOOKUP_TABLES or ASSESSMENT_TABLES
 *   // 2. Bump SCHEMA_VERSION
 *   // 3. Deploy - all users will upgrade automatically
 */

import type { StoreSchema } from './indexed-db.types';

// =============================================================================
// VERSION - Bump this when schema changes
// =============================================================================

/**
 * Current schema version for the lookup database (symphony-web-lookup). Increment this number when adding/modifying
 * lookup tables.
 *
 * Version History:
 *
 * - V1: Initial schema with 32 lookup tables
 * - V2: Added encrypted: false to all lookup tables (switched to opt-out encryption model)
 * - V3: (previous)
 * - V4: Added cpt2-codes table
 * - V5: Fixed cpt2-codes primaryKey/indexes to match API response (cpt2CodeID, cpt2Description)
 * - V6: icd-codes records now include lobName field; invalidate stale cache for client-side LOB filtering
 * - V7: Added treatment-plan-options table
 * - V8: icd-codes table now indexes icdVersion (ICD-10/11) and lobName for server-side filtering support
 * - V9: Added assessment-archival-reasons table
 * - V10: Added drug-dosing, drug-conditions, drug-allergies, drug-side-effects, and drug-drug-interactions tables
 * - V11: Kept only drug-codes, drug-dosing, and drug-conditions in offline cache; heavy clinical tables are live API
 * - V12: Fixed drug-codes primaryKey to 'code' (API response has no drugCodeID field). IndexedDB auto-clears on first
 *   access.
 * - V13: Removed effectiveDate, termDate, and isShow from icd-codes indexes (not needed for Dexie predicate)
 * - V14: Re-added effectiveDate, termDate, isShow, hccCodeId indexes to icd-codes for IndexedDB range queries
 *   (performance optimization) — later determined to be unused (V16)
 * - V15: Standardized ICD HCC field to hccCode and indexed hccCode (instead of hccCodeId)
 * - V16: Removed unused icd-codes indexes (icdVersion, effectiveDate, termDate, isShow, hccCode) - these fields are only
 *   used for in-memory filtering, not IndexedDB predicates
 * - V17: Removed drug-dosing (~4.25M rows) and drug-conditions (~3.5M rows) from the offline cache; both are now live API
 *   only
 * - V18: Restored drug-codes primary key to drugCodeID after the lookup API added the numeric identity field.
 * - V19: Added static dose-forms and dose-frequencies lookup tables.
 * - V20: Added new lookup table for audit categories
 * - V21: Changed drug-codes to unique medication-name rows keyed by medicationName; NDC fields are no longer cached.
 * - V22: Reverted drug-codes primary key to drugCodeId and indexed medicationName for optimized lookups.
 * - V23: Removed loinc-codes from the offline cache; it is now live API only. And removed few icd-codes indexes to
 *   increase load performance.
 */
export const LOOKUP_SCHEMA_VERSION = 23;

/**
 * Current schema version for the assessment database (symphony-web-assessment). Increment this number when
 * adding/modifying assessment tables.
 *
 * Version History:
 *
 * - V1: Initial schema with all assessment tables (encrypted PHI)
 * - V2: Added download transaction tables (download-transactions, download-staging)
 * - V3: Changed global-variables to per-assessment (primaryKey: assessmentId)
 * - V4: Added offline-ready table for tracking downloaded assessments
 * - V5: Added verification-status table for offline verification data
 * - V6: Added id-remap table (local↔server ID reconciliation) and sync-dead-letter table
 * - V7: Added impersonation-session-assessments table for session-scoped cleanup tracking
 * - V8: Added cedis and ucc tables for assessment-scoped Medley cache
 * - V9: Added dashboard-cache and appointments-list tables for offline dashboard & appointments fallback
 * - V10: Changed claims, cedis, ucc, and medications table primaryKey from assessmentId to memberAppointmentId; added
 *   assessmentId index on each for lookups by assessment (per-member caching)
 * - V11: Multi-user database partitioning (getUserAssessmentDbName) & multi-tenant composite primary keys (compositeKey)
 * - V12: Added template-clinical-rules table
 * - V13: Added clinical-summary (Patient Conditions card) and endpoint-cache (extensible future API cache)
 * - V14: Added encrypted completion-snapshots and sync-events without changing existing primary keys
 */
export const ASSESSMENT_SCHEMA_VERSION = 14;

/**
 * Current schema version for the medication database (symphony-web-medication). Increment this number when
 * adding/modifying medication tables.
 *
 * Version History:
 *
 * - V1: Initial schema with medication cache table
 * - V2: Added claims cache table for offline claims data
 * - V3: Added cedis and ucc cache tables for offline CEDIS and UCC diagnosis data
 * - V4: Changed claims table primaryKey from assessmentId to memberAppointmentId for per-member caching
 * - V5: Added multi-user physical database isolation (getUserMedicationDbName)
 */
export const MEDICATION_SCHEMA_VERSION = 5;

/** Schema version for the route-keyed public disclaimer cache. */
export const DISCLAIMER_SCHEMA_VERSION = 1;

// =============================================================================
// DATABASE NAMES
// =============================================================================

/** Database name for lookup/reference data (ICD codes, CPT codes, etc.) */
export const LOOKUP_DB_NAME = 'symphony-web-lookup';

/** Database name for assessment PHI data */
export const ASSESSMENT_DB_NAME = 'symphony-web-assessment';

/** Database name for medication cache */
export const MEDICATION_DB_NAME = 'symphony-web-medication';

export const DISCLAIMER_DB_NAME = 'symphony-web-disclaimers';

export const DISCLAIMER_TABLES: readonly { name: string; schema: StoreSchema }[] = [
  { name: 'disclaimers', schema: { primaryKey: 'route', encrypted: false } },
];

/** Safely extracts the active user ID from session storage. Used for dynamic database naming across MFE bundles. */
export function getCurrentUserId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem('conductor.auth.session');
    if (raw) {
      const session = JSON.parse(raw);
      if (session?.userProfile?.userId) {
        return String(session.userProfile.userId);
      }
    }
  } catch {
    // Ignore storage errors
  }
  return null;
}

/**
 * Returns user-scoped database name for assessment PHI data. Multi-user isolation: Each user receives their own
 * physical IndexedDB instance (e.g. 'symphony-web-assessment_u101') so that multiple users on the same tablet have
 * separate encryption contexts and never fail NaCl MAC verification.
 */
export function getUserAssessmentDbName(userId?: number | string | null): string {
  const uid = userId ?? getCurrentUserId();
  return uid ? `${ASSESSMENT_DB_NAME}_u${uid}` : ASSESSMENT_DB_NAME;
}

/** Returns user-scoped database name for medication cache. */
export function getUserMedicationDbName(userId?: number | string | null): string {
  const uid = userId ?? getCurrentUserId();
  return uid ? `${MEDICATION_DB_NAME}_u${uid}` : MEDICATION_DB_NAME;
}

/** Build a tenant-scoped composite key: `${clientId}:${id}`. Fallback to `${id}` if clientId is omitted. */
export function buildTenantAssessmentKey(clientId: number | string | null | undefined, id: number | string): string {
  if (clientId !== undefined && clientId !== null && Number(clientId) > 0) {
    return `${clientId}:${id}`;
  }
  return String(id);
}

// =============================================================================
// CACHE / RETENTION POLICY (shared across services)
// =============================================================================

/** Shared cache expiry policy (milliseconds). Use these values in services instead of hardcoded durations. */
export const CACHE_EXPIRY_MS = {
  /** Default cache expiry for generic tables: 7 days */
  DEFAULT: 7 * 24 * 60 * 60 * 1000,
  /** Lookup cache expiry: 30 days */
  LOOKUP: 30 * 24 * 60 * 60 * 1000,
  /** Assessment cache expiry: 48 hours */
  ASSESSMENT: 48 * 60 * 60 * 1000,
  /** Medication cache expiry: 1 hour */
  MEDICATION: 60 * 60 * 1000,
} as const;

/** Download transaction lifecycle policy (milliseconds). */
export const DOWNLOAD_RETENTION_MS = {
  /** In-progress transaction is considered stale after 5 minutes */
  STALE_THRESHOLD: 5 * 60 * 1000,
  /** Completed/failed transaction retention: 24 hours */
  COMPLETED: 24 * 60 * 60 * 1000,
  /** Stale in-progress transaction hard retention: 1 hour */
  STALE_IN_PROGRESS: 60 * 60 * 1000,
} as const;

// =============================================================================
// LOOKUP DATABASE TABLES (symphony-web-cache)
// =============================================================================

/**
 * All lookup tables with their schemas. These are reference data tables from /api/Lookup/lookup-configs. NOT encrypted
 * (encrypted: false) - contains public reference data only, no PHI.
 *
 * IMPORTANT: When adding a new table, bump LOOKUP_SCHEMA_VERSION!
 *
 * Schema columns are derived from actual API responses:
 *
 * - Primary key: typically ends with 'Id' (e.g., IcdCodeId)
 * - Indexes: labelColumn from config + common search fields
 * - Encrypted: false (lookup tables contain no PHI)
 */
export const LOOKUP_TABLES: ReadonlyArray<{ name: string; schema: StoreSchema }> = [
  // ─── Medical Codes ───────────────────────────────────────────────────────────
  {
    name: 'icd-codes',
    schema: {
      primaryKey: 'icdCodeID',
      indexes: ['code'],
      encrypted: false,
    },
  },
  {
    name: 'cpt-codes',
    schema: { primaryKey: 'cptCodeID', indexes: ['code', 'cptDescription'], encrypted: false },
  },
  {
    name: 'cpt2-codes',
    schema: { primaryKey: 'cpt2CodeID', indexes: ['code', 'cpt2Description'], encrypted: false },
  },
  {
    name: 'cpt-filters',
    schema: {
      primaryKey: 'cptFilterID',
      indexes: ['cptCode', 'cptCodeDescription', 'codeWithDescription'],
      encrypted: false,
    },
  },
  {
    name: 'modifier-codes',
    schema: {
      primaryKey: 'modifierCodeID',
      indexes: ['code', 'modifierDescription', 'codeWithDescription'],
      encrypted: false,
    },
  },

  // ─── Medication Lookups ──────────────────────────────────────────────────────
  {
    name: 'drug-codes',
    schema: { primaryKey: 'drugCodeId', indexes: ['medicationName'], encrypted: false },
  },
  {
    name: 'dose-types',
    schema: { primaryKey: 'doseTypeId', indexes: ['doseTypeDescription'], encrypted: false },
  },
  {
    name: 'dose-forms',
    schema: { primaryKey: 'doseTypeId', indexes: ['doseTypeDescription'], encrypted: false },
  },
  {
    name: 'dose-frequencies',
    schema: { primaryKey: 'doseFrequencyID', indexes: ['doseFrequency', 'description'], encrypted: false },
  },
  {
    name: 'medication-statuses',
    schema: { primaryKey: 'medicationStatusId', indexes: ['medicationStatusDescription'], encrypted: false },
  },
  {
    name: 'conditions',
    schema: { primaryKey: 'conditionID', indexes: ['conditionDescription'], encrypted: false },
  },
  {
    name: 'compliance-statuses',
    schema: { primaryKey: 'complianceStatusId', indexes: ['complianceStatusDescription'], encrypted: false },
  },
  {
    name: 'reasons',
    schema: { primaryKey: 'reasonId', indexes: ['reasonDescription'], encrypted: false },
  },

  // ─── Assessment Lookups ──────────────────────────────────────────────────────
  {
    name: 'assessment-types',
    schema: { primaryKey: 'assessmentTypeId', indexes: ['assessmentTypeDescription'], encrypted: false },
  },
  {
    name: 'assessment-statuses',
    schema: { primaryKey: 'assessmentStatusId', indexes: ['assessmentStatusDescription'], encrypted: false },
  },
  {
    name: 'assessment-archival-reasons',
    schema: {
      primaryKey: 'assessmentArchivalReasonId',
      indexes: ['assessmentArchivalReasonDescription'],
      encrypted: false,
    },
  },
  {
    name: 'assessment-strategies',
    schema: {
      primaryKey: 'assessmentStrategyId',
      indexes: ['componentName', 'assessmentStrategyTypeId'],
      encrypted: false,
    },
  },
  {
    name: 'assessment-strategy-types',
    schema: {
      primaryKey: 'assessmentStrategyTypeId',
      indexes: ['strategyTypeDescription', 'strategyTypeCode'],
      encrypted: false,
    },
  },
  {
    name: 'outcome-gaps',
    schema: { primaryKey: 'gapTypeId', indexes: ['gapLabel', 'measureCode'], encrypted: false },
  },

  // ─── Appointment & Scheduling ────────────────────────────────────────────────
  {
    name: 'appointment-statuses',
    schema: { primaryKey: 'appointmentStatusId', indexes: ['appointmentStatusDescription'], encrypted: false },
  },
  {
    name: 'location-decline-reasons',
    schema: { primaryKey: 'locationDeclineReasonId', indexes: ['reasonDescription'], encrypted: false },
  },

  // ─── Organization & Users ────────────────────────────────────────────────────
  {
    name: 'clients',
    schema: { primaryKey: 'clientId', indexes: ['clientName'], encrypted: false },
  },
  {
    name: 'vendors',
    schema: { primaryKey: 'vendorId', indexes: ['vendorName'], encrypted: false },
  },
  {
    name: 'user-types',
    schema: { primaryKey: 'userTypeId', indexes: ['userTypeDescription'], encrypted: false },
  },

  // ─── Provider & Practitioner ─────────────────────────────────────────────────
  {
    name: 'practitioner-specialties',
    schema: { primaryKey: 'practitionerSpecialtyId', indexes: ['practitionerSpecialtyName'], encrypted: false },
  },
  {
    name: 'provider-sources',
    schema: { primaryKey: 'providerSourceId', indexes: ['providerSourceDescription'], encrypted: false },
  },

  // ─── Insurance & Plans ───────────────────────────────────────────────────────
  {
    name: 'plan-type-lobs',
    schema: { primaryKey: 'lobId', indexes: ['displayName', 'lob'], encrypted: false },
  },
  {
    name: 'intervention-types',
    schema: { primaryKey: 'interventionTypeId', indexes: ['interventionTypeDescription'], encrypted: false },
  },
  {
    name: 'incentive-types',
    schema: { primaryKey: 'incentiveTypeId', indexes: ['incentiveTypeDescription'], encrypted: false },
  },
  {
    name: 'treatment-plan-options',
    schema: { primaryKey: 'treatmentPlanOptionId', indexes: ['treatmentPlanName', 'description'], encrypted: false },
  },

  // ─── General Reference ───────────────────────────────────────────────────────
  {
    name: 'states',
    schema: { primaryKey: 'stateId', indexes: ['stateName', 'stateCode'], encrypted: false },
  },
  {
    name: 'languages',
    schema: { primaryKey: 'languageId', indexes: ['description'], encrypted: false },
  },
  {
    name: 'phone-types',
    schema: { primaryKey: 'phoneTypeId', indexes: ['phoneTypeDescription'], encrypted: false },
  },
  {
    name: 'association-types',
    schema: { primaryKey: 'associationTypeId', indexes: ['associationTypeDescription'], encrypted: false },
  },
  {
    name: 'template-statuses',
    schema: { primaryKey: 'templateStatusId', indexes: ['templateStatusDescription'], encrypted: false },
  },

  // ─── Offline Authentication ──────────────────────────────────────────────────
  {
    name: 'offline-session',
    schema: { primaryKey: 'id', indexes: [], encrypted: false },
  },
  {
    name: 'offline-pin',
    schema: { primaryKey: 'userId', indexes: [], encrypted: false },
  },
  {
    name: 'audit-statuses',
    schema: { primaryKey: 'auditStatusId', indexes: ['auditStatusDescription'], encrypted: false },
  },
  {
    name: 'audit-types',
    schema: { primaryKey: 'auditTypeId', indexes: ['auditTypeDescription'], encrypted: false },
  },
];

// =============================================================================
// ASSESSMENT DATABASE TABLES (symphony-web-assessment)
// =============================================================================

/** Table name constants for assessment database */
export const ASSESSMENT_TABLE_NAMES = {
  ASSESSMENT: 'assessment',
  MEMBER_INFO: 'member-info',
  QUESTIONNAIRE: 'questionnaire',
  RESPONSE: 'response',
  CLAIMS: 'claims',
  CEDIS: 'cedis',
  UCC: 'ucc',
  MEDICATIONS: 'medications',
  GLOBAL_VARS: 'global-variables',
  PENDING_SYNC: 'pending-sync',
  // Download transaction tables
  DOWNLOAD_TRANSACTIONS: 'download-transactions',
  DOWNLOAD_STAGING: 'download-staging',
  // Offline ready flag table
  OFFLINE_READY: 'offline-ready',
  // Verification status for member-info page
  VERIFICATION_STATUS: 'verification-status',
  // Local ID ↔ server ID reconciliation (for offline/impersonation creates)
  ID_REMAP: 'id-remap',
  // Sync operations that exceeded max retries
  SYNC_DEAD_LETTER: 'sync-dead-letter',
  // Session-scoped list of assessments touched during impersonation
  IMPERSONATION_SESSION_ASSESSMENTS: 'impersonation-session-assessments',
  // Dashboard offline cache (NP profile, overdue, calendar, audit score)
  DASHBOARD_CACHE: 'dashboard-cache',
  // Filter options cache for offline appointments page
  FILTER_OPTIONS: 'filter-options',
  // Appointments list offline cache
  APPOINTMENTS_LIST: 'appointments-list',
  // Resolved template clinical rules required for offline assessment evaluation
  TEMPLATE_CLINICAL_RULES: 'template-clinical-rules',
  // Clinical summary for member-info offline Patient Conditions card
  CLINICAL_SUMMARY: 'clinical-summary',
  // Generic endpoint cache for auto-including future page-level assessment APIs
  ENDPOINT_CACHE: 'endpoint-cache',
  COMPLETION_SNAPSHOTS: 'completion-snapshots',
  SYNC_EVENTS: 'sync-events',
} as const;

/**
 * Assessment tables keyed by `assessmentId` that store per-assessment payloads.
 *
 * Keep this list aligned with any new per-assessment data tables so cleanup/discovery logic can consume a single shared
 * source of truth.
 */
export const ASSESSMENT_KEYED_TABLES = [
  ASSESSMENT_TABLE_NAMES.ASSESSMENT,
  ASSESSMENT_TABLE_NAMES.MEMBER_INFO,
  ASSESSMENT_TABLE_NAMES.QUESTIONNAIRE,
  ASSESSMENT_TABLE_NAMES.RESPONSE,
  ASSESSMENT_TABLE_NAMES.CLAIMS,
  ASSESSMENT_TABLE_NAMES.CEDIS,
  ASSESSMENT_TABLE_NAMES.UCC,
  ASSESSMENT_TABLE_NAMES.MEDICATIONS,
  ASSESSMENT_TABLE_NAMES.GLOBAL_VARS,
  ASSESSMENT_TABLE_NAMES.OFFLINE_READY,
  ASSESSMENT_TABLE_NAMES.VERIFICATION_STATUS,
  ASSESSMENT_TABLE_NAMES.TEMPLATE_CLINICAL_RULES,
  ASSESSMENT_TABLE_NAMES.CLINICAL_SUMMARY,
  ASSESSMENT_TABLE_NAMES.ENDPOINT_CACHE,
] as const;

/** All assessment tables with their schemas. These contain PHI/sensitive data - marked as encrypted. */
export const ASSESSMENT_TABLES: ReadonlyArray<{ name: string; schema: StoreSchema }> = [
  {
    name: ASSESSMENT_TABLE_NAMES.COMPLETION_SNAPSHOTS,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId'], encrypted: true },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.SYNC_EVENTS,
    schema: { primaryKey: 'eventId', indexes: ['assessmentId', 'clientId', 'operationId'], encrypted: true },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.ASSESSMENT,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId'], encrypted: true },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.MEMBER_INFO,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId'], encrypted: true },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.QUESTIONNAIRE,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId'], encrypted: true },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.RESPONSE,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId'], encrypted: true },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.CLAIMS,
    schema: {
      primaryKey: 'compositeKey',
      indexes: ['assessmentId', 'clientAssessmentId', 'memberAppointmentId', 'clientId'],
      encrypted: true,
    },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.CEDIS,
    schema: {
      primaryKey: 'compositeKey',
      indexes: ['assessmentId', 'clientAssessmentId', 'memberAppointmentId', 'clientId'],
      encrypted: true,
    },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.UCC,
    schema: {
      primaryKey: 'compositeKey',
      indexes: ['assessmentId', 'clientAssessmentId', 'memberAppointmentId', 'clientId'],
      encrypted: true,
    },
  },
  {
    // DEPRECATED: Medications now handled by MedicationDataService (symphony-web-medication DB)
    // Table kept for backwards compatibility / migration cleanup
    name: ASSESSMENT_TABLE_NAMES.MEDICATIONS,
    schema: {
      primaryKey: 'compositeKey',
      indexes: ['assessmentId', 'clientAssessmentId', 'memberAppointmentId', 'clientId'],
      encrypted: true,
    },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.GLOBAL_VARS,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId'], encrypted: true }, // Per-assessment global variables
  },
  {
    name: ASSESSMENT_TABLE_NAMES.PENDING_SYNC,
    schema: { primaryKey: '++id', indexes: ['assessmentId', 'clientId', 'timestamp'], encrypted: true },
  },
  // Download transaction tracking tables
  {
    name: ASSESSMENT_TABLE_NAMES.DOWNLOAD_TRANSACTIONS,
    schema: { primaryKey: 'txId', indexes: ['status', 'startTime', 'clientId'], encrypted: false },
  },
  {
    name: ASSESSMENT_TABLE_NAMES.DOWNLOAD_STAGING,
    schema: { primaryKey: 'stagingId', indexes: ['txId', 'assessmentId', 'clientId'], encrypted: true },
  },
  // Offline ready flag - marks assessments that completed full download
  {
    name: ASSESSMENT_TABLE_NAMES.OFFLINE_READY,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId', 'downloadedAt'], encrypted: false },
  },
  // Verification status for member-info page (offline resume)
  {
    name: ASSESSMENT_TABLE_NAMES.VERIFICATION_STATUS,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId'], encrypted: true },
  },
  // Resolved rules are assessment-scoped so downloaded assessments retain deterministic offline evaluation.
  {
    name: ASSESSMENT_TABLE_NAMES.TEMPLATE_CLINICAL_RULES,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId'], encrypted: true },
  },
  // Local ID ↔ server ID reconciliation for offline/impersonation creates
  {
    name: ASSESSMENT_TABLE_NAMES.ID_REMAP,
    schema: { primaryKey: 'localId', indexes: ['resourceType', 'createdAt'], encrypted: false },
  },
  // Sync operations that exceeded max retries — user-visible dead letter queue
  {
    name: ASSESSMENT_TABLE_NAMES.SYNC_DEAD_LETTER,
    schema: { primaryKey: '++id', indexes: ['assessmentId', 'clientId', 'failedAt'], encrypted: true },
  },
  // Session-scoped tracking for impersonation cleanup
  {
    name: ASSESSMENT_TABLE_NAMES.IMPERSONATION_SESSION_ASSESSMENTS,
    schema: { primaryKey: 'assessmentId', indexes: ['updatedAt'], encrypted: false },
  },
  // Dashboard offline cache
  {
    name: ASSESSMENT_TABLE_NAMES.DASHBOARD_CACHE,
    schema: { primaryKey: 'key', indexes: ['timestamp'], encrypted: true },
  },
  // Filter options offline cache
  {
    name: ASSESSMENT_TABLE_NAMES.FILTER_OPTIONS,
    schema: { primaryKey: 'key', encrypted: false },
  },
  // Appointments list offline cache
  {
    name: ASSESSMENT_TABLE_NAMES.APPOINTMENTS_LIST,
    schema: { primaryKey: 'key', indexes: ['timestamp'], encrypted: true },
  },
  // Clinical summary for member-info offline Patient Conditions card
  {
    name: ASSESSMENT_TABLE_NAMES.CLINICAL_SUMMARY,
    schema: { primaryKey: 'compositeKey', indexes: ['assessmentId', 'clientId'], encrypted: true },
  },
  // Generic endpoint cache for auto-including future page-level assessment APIs
  {
    name: ASSESSMENT_TABLE_NAMES.ENDPOINT_CACHE,
    schema: {
      primaryKey: 'compositeKey',
      indexes: ['assessmentId', 'clientId', 'endpointKey'],
      encrypted: true,
    },
  },
];

// =============================================================================
// MEDICATION DATABASE TABLES (symphony-web-medication)
// =============================================================================

/** Table name constants for medication database */
export const MEDICATION_TABLE_NAMES = {
  MEDICATIONS: 'medications',
  CLAIMS: 'claims',
  CEDIS: 'cedis',
  UCC: 'ucc',
} as const;

/** All medication tables with their schemas. Assessment-scoped medication data - encrypted for PHI protection. */
export const MEDICATION_TABLES: ReadonlyArray<{ name: string; schema: StoreSchema }> = [
  {
    name: MEDICATION_TABLE_NAMES.MEDICATIONS,
    schema: {
      primaryKey: 'assessmentId',
      indexes: ['fetchedAt'],
      encrypted: true,
    },
  },
  {
    name: MEDICATION_TABLE_NAMES.CLAIMS,
    schema: {
      primaryKey: 'memberAppointmentId',
      indexes: ['assessmentId', 'fetchedAt'],
      encrypted: true,
    },
  },
  {
    name: MEDICATION_TABLE_NAMES.CEDIS,
    schema: {
      primaryKey: 'assessmentId',
      indexes: ['fetchedAt'],
      encrypted: true,
    },
  },
  {
    name: MEDICATION_TABLE_NAMES.UCC,
    schema: {
      primaryKey: 'assessmentId',
      indexes: ['fetchedAt'],
      encrypted: true,
    },
  },
];

// =============================================================================
// MEDLEY DATABASE - Aliases for MedleyDataService
// =============================================================================

/**
 * Medley database aliases - point to the same medication database. Used by MedleyDataService in @complexcare/medley
 * package.
 */
export const MEDLEY_DB_NAME = MEDICATION_DB_NAME;
export const MEDLEY_SCHEMA_VERSION = MEDICATION_SCHEMA_VERSION;
export const MEDLEY_TABLE_NAMES = MEDICATION_TABLE_NAMES;
export const MEDLEY_TABLES = MEDICATION_TABLES;

// =============================================================================
// Helpers & Alias Resolution
// =============================================================================

/** Canonical lookup aliases map (e.g. 'icd' -> 'icd-codes') */
export const LOOKUP_ALIAS_MAP: Readonly<Record<string, string>> = {
  icd: 'icd-codes',
  cpt: 'cpt-codes',
  cpt2: 'cpt2-codes',
  modifier: 'modifier-codes',
};

/** Normalize lookup alias keys to canonical table names. */
export function normalizeLookupName(name: string): string {
  if (!name) return name;
  return LOOKUP_ALIAS_MAP[name.toLowerCase()] ?? name;
}

/** Get schema for a lookup table by name. Returns undefined if table is not in the registry. */
export function getLookupTableSchema(tableName: string): StoreSchema | undefined {
  const canonical = normalizeLookupName(tableName);
  return LOOKUP_TABLES.find((t) => t.name === canonical)?.schema;
}

/** Check if a lookup table is registered. */
export function isRegisteredLookupTable(tableName: string): boolean {
  const canonical = normalizeLookupName(tableName);
  return LOOKUP_TABLES.some((t) => t.name === canonical);
}

/** Get all registered lookup table names. */
export function getRegisteredLookupTableNames(): string[] {
  return LOOKUP_TABLES.map((t) => t.name);
}
